import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { ArrowRight, ArrowUpRight } from 'lucide-react'

import {
    motion,
    useMotionValue,
    useScroll,
    useTransform
} from 'motion/react'

import { cn } from '@/lib/utils'
import { useMediaQuery } from '@/hooks/use-media-query'
import { useReduceMotion } from '@/hooks/use-reduce-motion'
import { arrowClass, buttonClass } from '@/components/generals/button'
import Magnetic from '@/components/generals/magnetic'
import SmartLink from '@/components/generals/smart-link'
import ProjectCard from '@/components/landing/project-card'
import { work } from '@/constants/content'

// Featured work. On desktop the section pins in place and the projects slide past sideways as you
// scroll down. On phones, tablets and for reduced motion it is a simple vertical list.
const Work: FC = (): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const trackRef = useRef<HTMLDivElement>(null)
    const reduce = useReduceMotion()
    const isDesktop = useMediaQuery('(min-width: 1024px)')
    const horizontal = isDesktop && !reduce
    const [distance, setDistance] = useState(0)
    const travel = useMotionValue(0)

    const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start start', 'end end'] })
    const x = useTransform(() => -scrollYProgress.get() * travel.get())

    useEffect(() => {
        const track = trackRef.current

        if (!horizontal || !track) {
            setDistance(0)
            travel.set(0)

            return
        }

        const measure = () => {
            const next = Math.max(0, Math.round(track.scrollWidth - document.documentElement.clientWidth))

            setDistance(next)
            travel.set(next)
        }

        measure()

        const observer = new ResizeObserver(measure)

        observer.observe(track)
        window.addEventListener('resize', measure)

        return () => {
            observer.disconnect()
            window.removeEventListener('resize', measure)
        }
    }, [horizontal, travel])

    const heading = (
        <div className={cn(horizontal ? 'w-[32vw] shrink-0' : 'max-w-[40rem]')}>
            <p className='eyebrow text-paper/70'>
                {String(work.projects.length).padStart(2, '0')}
            </p>

            <h2 id='work-heading' className={cn('mt-4 font-heading font-bold leading-[0.86] tracking-[-0.015em] wdth-78', horizontal ? 'text-[7.4vw]' : 'text-[16vw] sm:text-[12vw] lg:text-[8vw]')}>
                {work.heading}
            </h2>

            <p className='mt-6 max-w-[26rem] text-lg leading-relaxed text-paper/80 sm:text-xl'>
                {work.intro}
            </p>
        </div>
    )

    return (
        <section
            ref={sectionRef}
            id='work'
            aria-labelledby='work-heading'
            className='relative bg-ink text-paper [--focus-ring:var(--paper)]'
            style={horizontal && distance > 0 ? { height: `calc(100vh + ${distance}px)` } : undefined}
        >
            {horizontal ? (
                <div className='sticky top-0 h-screen overflow-hidden'>
                    <motion.div
                        ref={trackRef}
                        style={{ x }}
                        className='flex h-full w-max items-center gap-[6vw] pl-12 pr-[6vw] pt-10'
                    >
                        {heading}

                        {work.projects.map((project, index) => (
                            <ProjectCard
                                key={project.slug}
                                project={project}
                                index={index}
                                className='w-[min(54vw,calc((100vh_-_16rem)*1.6))] shrink-0'
                            />
                        ))}

                        <div className='flex w-[22vw] shrink-0 items-center justify-center'>
                            <Magnetic strength={0.4}>
                                <SmartLink
                                    href='/work'
                                    className='group grid size-52 place-items-center rounded-full border-2 border-paper bg-blaze text-center font-heading text-2xl font-extrabold leading-tight text-ink transition-[scale] duration-500 ease-bounce hover:scale-105 [--focus-ring:var(--paper)] xl:size-60'
                                >
                                    <span className='flex flex-col items-center gap-2'>
                                        {work.cta}
                                        <ArrowUpRight aria-hidden='true' className='size-7 transition-transform duration-500 ease-bounce group-hover:rotate-45' />
                                    </span>
                                </SmartLink>
                            </Magnetic>
                        </div>
                    </motion.div>

                    <div aria-hidden='true' className='absolute inset-x-12 bottom-8 h-[3px] rounded-full bg-paper/15'>
                        <motion.div style={{ scaleX: scrollYProgress }} className='h-full origin-left rounded-full bg-blaze' />
                    </div>
                </div>
            ) : (
                <div className='shell py-24 sm:py-32'>
                    {heading}

                    <div className='mt-14 grid gap-16 sm:mt-20 lg:grid-cols-2 lg:gap-x-12'>
                        {work.projects.map((project, index) => (
                            <ProjectCard key={project.slug} project={project} index={index} />
                        ))}
                    </div>

                    <SmartLink
                        href='/work'
                        className={buttonClass({ variant: 'blaze', size: 'lg', shadow: 'paper', className: 'mt-16 w-full border-paper sm:w-auto' })}
                    >
                        {work.cta}
                        <ArrowRight aria-hidden='true' className={arrowClass} />
                    </SmartLink>
                </div>
            )}
        </section>
    )
}

export default Work
